/**
 * SelectionPlane — the dynamic plane's selected-object layer.
 *
 * Renders INSIDE the world transform: the selected-objects picture plus the
 * selection frame (outline + 8 resize handles + rotate handle stem), all
 * wrapped in a `<Group>` whose translation derives from the DRAG shared
 * values. During a drag only this group's transform mutates (UI thread) —
 * the static plane and React never move. On commit the store patch lands
 * and the drag offset resets in the same JS tick, so the frame paints once
 * with both applied.
 *
 * Handle sizes divide by `zoom` (a settle-time snapshot) so they stay
 * roughly constant on screen; mid-pinch they scale with the world until the
 * next settle — an accepted v1 trade-off (a fully zoom-locked handle needs
 * per-handle derived values).
 *
 * @module floorplan/renderer/SelectionPlane
 * @category FloorPlan
 */

import type { SharedValue } from 'react-native-reanimated';
import { useDerivedValue } from 'react-native-reanimated';
import type { SkPicture } from '@shopify/react-native-skia';
import { Circle, Group, Picture, Rect } from '@shopify/react-native-skia';

import {
  HANDLE_VISUAL_SIZE_PX,
  ROTATE_HANDLE_VISUAL_SIZE_PX,
} from '../constants';
import type { Bounds } from '../models/geometry';
import { rotateHandleOffset } from '../selection/handles';

type Props = {
  /** Recorded picture of the SELECTED objects (null = empty selection). */
  picture: SkPicture | null;
  /** Selection frame (union AABB of the selection) in world coords. */
  frame: Bounds | null;
  /** Zoom snapshot from the last camera settle (handle sizing). */
  zoom: number;
  /** Selection color (theme.selection). */
  color: string;
  dragDx: SharedValue<number>;
  dragDy: SharedValue<number>;
  /** Live resize scale about the anchor (identity 1 when idle). */
  dragSx: SharedValue<number>;
  dragSy: SharedValue<number>;
  anchorX: SharedValue<number>;
  anchorY: SharedValue<number>;
  /** Live rotation (degrees) about the rotation center (0 when idle). */
  dragRotation: SharedValue<number>;
  rotCenterX: SharedValue<number>;
  rotCenterY: SharedValue<number>;
};

const SelectionPlane = ({
  picture,
  frame,
  zoom,
  color,
  dragDx,
  dragDy,
  dragSx,
  dragSy,
  anchorX,
  anchorY,
  dragRotation,
  rotCenterX,
  rotCenterY,
}: Props) => {
  // Compose move ∘ rotate(center) ∘ scale(anchor). Only one interaction is
  // live at a time; the others sit at identity, so the composition is safe.
  const transform = useDerivedValue(() => {
    const rad = (dragRotation.get() * Math.PI) / 180;
    return [
      { translateX: dragDx.get() },
      { translateY: dragDy.get() },
      { translateX: rotCenterX.get() },
      { translateY: rotCenterY.get() },
      { rotate: rad },
      { translateX: -rotCenterX.get() },
      { translateY: -rotCenterY.get() },
      { translateX: anchorX.get() },
      { translateY: anchorY.get() },
      { scaleX: dragSx.get() },
      { scaleY: dragSy.get() },
      { translateX: -anchorX.get() },
      { translateY: -anchorY.get() },
    ];
  });

  if (picture === null && frame === null) return null;

  const handleSize = HANDLE_VISUAL_SIZE_PX / zoom;
  const strokeWidth = 1.5 / zoom;

  const handles: { id: string; x: number; y: number }[] = [];
  if (frame !== null) {
    const xs = [frame.x, frame.x + frame.width / 2, frame.x + frame.width];
    const ys = [frame.y, frame.y + frame.height / 2, frame.y + frame.height];
    for (let col = 0; col < xs.length; col += 1) {
      for (let row = 0; row < ys.length; row += 1) {
        // Skip the center — 8 perimeter handles.
        if (col === 1 && row === 1) continue;
        const x = xs[col];
        const y = ys[row];
        if (x === undefined || y === undefined) continue;
        handles.push({ id: `${col}:${row}`, x, y });
      }
    }
  }

  return (
    <Group transform={transform}>
      {picture !== null ? <Picture picture={picture} /> : null}
      {frame !== null ? (
        <>
          <Rect
            x={frame.x}
            y={frame.y}
            width={frame.width}
            height={frame.height}
            color={color}
            style="stroke"
            strokeWidth={strokeWidth}
          />
          {handles.map((handle) => (
            <Rect
              // Keyed by handle ID, NOT position — a degenerate frame (a
              // line's ~0-height selection) collapses several handles onto
              // the same coordinates, which made position keys collide.
              key={handle.id}
              x={handle.x - handleSize / 2}
              y={handle.y - handleSize / 2}
              width={handleSize}
              height={handleSize}
              color={color}
            />
          ))}
          {/* Rotate handle: floats above the top-center of the frame.
              Drawn at the SAME offset the hit test uses (rotateHandleOffset)
              so the visual and touch target stay aligned; larger than the
              resize squares (its own visual constant). */}
          <Circle
            cx={frame.x + frame.width / 2}
            cy={frame.y - rotateHandleOffset(zoom)}
            r={ROTATE_HANDLE_VISUAL_SIZE_PX / zoom / 2}
            color={color}
          />
        </>
      ) : null}
    </Group>
  );
};

export default SelectionPlane;
